import React, { useState, useEffect } from 'react';

const SupportModal = ({ isOpen, onClose }) => {
  
  const [currentView, setCurrentView] = useState('intro'); // 'intro', 'info', 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [donationAmount, setDonationAmount] = useState(80);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      // Check if user returned from successful payment
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('payment') === 'success') {
        setCurrentView('success');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setCurrentView('intro');
      }
      setIsLoading(false);
      setDonationAmount(80);
      setDonorName('');
      setDonorEmail('');
      // Small delay to ensure DOM is ready, then trigger animation
      setTimeout(() => {
        setShouldShow(true);
      }, 10);
    } else {
      setShouldShow(false);
    }
  }, [isOpen]);

  const handleDonationAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseFloat(value) >= 1)) {
      setDonationAmount(value);
    }
  };

  const handleNameChange = (e) => {
    setDonorName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setDonorEmail(e.target.value);
  };

  const handleContinueToPerks = () => {
    setCurrentView('info');
  };

  const handleClose = () => {
    setShouldShow(false);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 150); // Match the CSS transition duration
  };

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen && !shouldShow) return null;

  const amount = parseFloat(donationAmount) || 0;
  const hasTierThree = amount >= 40; // Code + Documentation
  const hasTierTwo = amount >= 80; // + Community
  const hasTierOne = amount >= 300; // + Personal Support

  const getTierText = () => {
    if (amount >= 300) return 'tier 1 partner';
    if (amount >= 80) return 'tier 2 donor';
    if (amount >= 40) return 'tier 3 donor';
    return 'supporter';
  };

  return (
    <div className={`modal ${shouldShow ? 'show' : ''}`} onClick={handleModalClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            {currentView === 'intro' && 'Support Barbotine'}
            {currentView === 'info' && 'Get perks!'}
            {currentView === 'success' && 'Payment successful'}
          </h2>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          {currentView === 'intro' && (
            <div className="intro-section">
              <div style={{
                textAlign: 'center',
                fontSize: '15px',
                lineHeight: '1.5',
                color: '#555',
                margin: '30px 20px',
                fontWeight: '400'
              }}>
                <p style={{ marginBottom: '16px' }}>
                  Barbotine is an open-source project that helps traders discover arbitrage opportunities in crypto markets.
                </p>
                <p style={{ marginBottom: '16px' }}>
                  By supporting our project, you not only help us continue developing and improving the tools, but you also unlock exclusive perks and premium features.
                </p>
              </div>
              
              <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <button 
                  onClick={handleContinueToPerks}
                  className="btn btn-primary" 
                  style={{ 
                    fontSize: '15px',
                    padding: '10px 28px',
                    fontWeight: '500'
                  }}
                >
                  Support Barbotine
                </button>
              </div>
            </div>
          )}

          {currentView === 'info' && (
          <div id="supportInfo">
            <div className="modal-two-columns">
              <div className="modal-left-column">
                <div style={{ 
                    textAlign: 'center', 
                    marginBottom: '10px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    With your current donation, you'd get:
                  </div>
                
                <div className="features">
                  <ul style={{ listStyleType: 'none', padding: 0 }}>
                    <li style={{ 
                      opacity: hasTierThree ? 1 : 0.5,
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      {hasTierThree && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      <div>
                        <strong>All the private code & all updates for a year</strong>
                        <br />
                        <p style={{ fontSize: 13, color: '#666' }}>
                          You will get access to all the modes of the arbitrage system, plus all the other private code & bots made by Barbotine.
                        </p>
                      </div>
                    </li>
                    <li style={{ 
                      opacity: hasTierThree ? 1 : 0.5,
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      {hasTierThree && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      <div>
                        <strong>All ressources and documentation</strong>
                        <br />
                        <p style={{ fontSize: 13, color: '#666' }}>
                          You will get access to all the documentation and available ressources of the arbitrage system, going deep into the concepts of arbitrage in crypto markets, risk management, performance optimization, and more.
                        </p>
                      </div>
                    </li>
                    <li style={{ 
                      opacity: hasTierTwo ? 1 : 0.5,
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      {hasTierTwo && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      <div>
                        <strong>Access to our private community</strong>
                        <br />
                        <p style={{ fontSize: 13, color: '#666' }}>
                          You will get access to a private community of beginners, project owners, experts, where you can ask questions, share your ideas, and connect together.
                        </p>
                      </div>
                    </li>
                    <li style={{ 
                      opacity: hasTierOne ? 1 : 0.5,
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      {hasTierOne && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginTop: '4px', flexShrink: 0 }}>
                          <path d="M20 6L9 17L4 12" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                      <div>
                        <strong>Personalized help by the founders</strong>
                        <br />
                        <p style={{ fontSize: 13, color: '#666' }}>
                          Need help about the bot, the code, or anything else? You can ask the founders directly, and we can arrange a call if needed.
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="modal-right-column">
                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '20px'
                }}>
                  <div style={{ width: '100%' }}>
                    <input
                      type="text"
                      value={donorName}
                      onChange={handleNameChange}
                      placeholder="Enter your name"
                      style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        width: '100%',
                        textAlign: 'left',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        boxSizing: 'border-box'
                      }}

                    />
                    <input
                      type="email"
                      value={donorEmail}
                      onChange={handleEmailChange}
                      placeholder="Enter your email"
                      style={{
                        padding: '8px 12px',
                        marginTop:10,
                        fontSize: '14px',
                        marginBottom: 20,
                        width: '100%',
                        textAlign: 'left',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        boxSizing: 'border-box'
                      }}

                    />
                  </div>
                </div>

                <div style={{ 
                  textAlign: 'center', 
                  marginBottom: '20px'
                }}>
                  <label style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '15px'
                  }}>
                    I want to donate 
                    <div style={{ position: 'relative', display: 'inline-block', margin: '8px auto 0 auto' }}>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={donationAmount}
                        onChange={handleDonationAmountChange}
                        style={{
                          padding: '8px 12px 8px 40px',
                          fontSize: '14px',
                          borderRadius: '0.375rem',
                          width: '80px',
                          textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          MozAppearance: 'textfield',
                          display: 'block'
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontSize: '14px',
                        pointerEvents: 'none'
                      }}>
                        EUR
                      </span>
                    </div>
                  </label>
                  <style>
                    {`
                      input[type=number]::-webkit-outer-spin-button,
                      input[type=number]::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                      }
                    `}
                  </style>
                </div>

                <div style={{
                    textAlign: 'center',
                    marginBottom: '20px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    You'd become a {getTierText()}.
                </div>

                <div style={{
                    textAlign: 'center',
                    marginBottom: '20px',
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    €40 or above is tier 3<br/>
                    €80 or above is tier 2<br/>
                    €300 or above is tier 1 partner
                </div>
                
                <button 
                  disabled={true}
                  className="btn btn-primary" 
                  style={{ width: '100%', margin: 0, fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  {isLoading ? (
                    <>
                      Loading...
                    </>
                  ) : (
                    <>
                      Contact me
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentView === 'success' && (
          <div className="success-container" style={{ display: 'block' }}>
            <div className="success-message">
              <div className="success-icon"></div>
              Welcome to the Barbotine family!
            </div>
            <div className="success-details">
              {hasTierOne ? 
                'Your payment is complete. Check your email for everything.' :
                'Thank you for your generous donation! Your support helps keep this project running and is deeply appreciated.'
              }
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default SupportModal; 
